import {
  cacheKeys,
  DayIntervalUnit,
  RoutineType,
  useCreateMutation,
  useCustomQuery,
  useEmbeddedRoutines,
  WorkspaceRoutine,
} from '@gripp/shared-logic';
import { useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import { MutableRefObject, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import LoadingIndicator from '../../loadingIndicator';
import { AlertForm } from './alertForm';
import { AlertFormRef } from './types';

const GET_GROUP_WITH_ROUTINES = `
  query GetGroupWithRoutines($id: ID!, $groupFilter: WorkspaceRoutineFilter!) {
    getGroup(id: $id) {
      id
      name
      workspace {
        id
        name
      }
    }
    findWorkspaceRoutines(filter: $groupFilter) {
      items {
        id
        name
        type
        order
        config
      }
    }
  }
`;

const CREATE_WORKFLOW = `
  mutation CreateWorkflow($input: CreateWorkflowInput!) {
    workflow: createWorkflow(input: $input) {
      id
      name
    }
  }
`;

type GroupAlertFormProps = {
  groupId: string;
  onClose: () => void;
  formRef?: MutableRefObject<AlertFormRef>;
  renderBottomComponent?: (onSubmit: () => void, disabled: boolean) => React.ReactNode;
};

export const GroupAlertForm = ({
  groupId,
  onClose,
  formRef,
  renderBottomComponent,
}: GroupAlertFormProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { data: groupData, isLoading } = useCustomQuery({
    query: GET_GROUP_WITH_ROUTINES,
    cacheKey: ['group-with-routines', groupId],
    variables: {
      id: groupId,
      groupFilter: { 
        groups: { eq: groupId },
        deactivated: { ne: true }
      },
    },
  });

  const [defaultObject, setDefaultObject] = useState<any>(null);
  const [counterRoutines, setCounterRoutines] = useState<WorkspaceRoutine[]>([]);
  const [availableRoutines, setAvailableRoutines] = useState<WorkspaceRoutine[]>([]);
  const [currentGroup, setCurrentGroup] = useState<any>(null);

  const directRoutines: WorkspaceRoutine[] = (groupData as any)?.findWorkspaceRoutines?.items || [];
  const { routinesWithEmbedded, isEmbeddedRoutinesLoading } = useEmbeddedRoutines(directRoutines);

  const { mutateAsync: createWorkflow } = useCreateMutation({
    modelName: 'workflow',
    query: CREATE_WORKFLOW,
  });

  useEffect(() => {
    if (!isLoading && !isEmbeddedRoutinesLoading && groupData) {
      const group = (groupData as any)?.getGroup;
      
      if (!group) {
        return;
      }
      
      setCurrentGroup(group);
      
      const counterRoutines = routinesWithEmbedded.filter(
        (routine: any) => routine.type === RoutineType.Counter
      );
      setCounterRoutines(counterRoutines);
      setAvailableRoutines(routinesWithEmbedded);

      const groupName = group?.name || '';
      const daysStartDate = moment().format('MM/DD/YYYY');

      const defaultValues = {
        alertLabel: '',
        alertLabelPlaceholder: `${t('alerts.alert')}: ${groupName}`,
        startDate: undefined,
        endDate: undefined,
        minValue: undefined,
        maxValue: undefined,
        alertType: undefined,
        secondAlertType: undefined,
        days: undefined,
        daysStartDate: daysStartDate,
        completions: undefined,
        daysSkipped: undefined,
        onDate: undefined,
        intervalRoutineId: undefined,
        onInterval: undefined,
        onIntervalStart: undefined,
        dayInterval: 1,
        dayIntervalUnit: DayIntervalUnit.Days,
      };

      setDefaultObject(defaultValues);
    }
  }, [isLoading, isEmbeddedRoutinesLoading, groupData, routinesWithEmbedded, t]);

  const submitAlert = async (input: any) => {
    await createWorkflow(
      { input },
      {
        onSuccess: async () => {
          // Invalidate groups list cache to refresh group alerts count
          await queryClient.invalidateQueries({
            queryKey: cacheKeys('group').lists(),
          });
          onClose();
        },
      }
    );
  };

  const isLoadingData = isLoading || isEmbeddedRoutinesLoading;

  return isLoadingData || !defaultObject || !currentGroup ? (
    <LoadingIndicator />
  ) : (
    <AlertForm
      defaultValues={defaultObject}
      counterRoutines={counterRoutines}
      group={currentGroup}
      availableRoutines={availableRoutines}
      submitAlert={submitAlert}
      onClose={onClose}
      headerTitle="alerts.newAlert"
      backButtonLabel={t('topNav.back')}
      openDropdownOnLoad={true}
      formRef={formRef}
      renderBottomComponent={renderBottomComponent}
      hideRoutineSelector
    />
  );
};
